/**
 * Card — a surface container (Claude Design `.card`): 14px radius, 1px border,
 * `surface` fill.
 *
 * Compose with the sub-components:
 *   - `Card.Head`   — bordered header row (`.card-head`)
 *   - `Card.Eyebrow`— uppercase tracking label (`.card-eyebrow`)
 *   - `Card.Title`  — section heading (defaults to `h2`)
 *   - `Card.Body`   — padded body (`.card-pad`)
 *   - `Card.Footer` — bordered actions row
 *
 * `Card.Header` is kept as an alias of `Card.Head` for existing call sites.
 */
import type { HTMLAttributes, ReactElement } from 'react';

import { cn } from './cn.js';

export type CardProps = HTMLAttributes<HTMLDivElement>;

export function Card({ className, ...props }: CardProps): ReactElement {
  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-surface shadow-card',
        className,
      )}
      {...props}
    />
  );
}

/** `.card-head` — a bordered header row (flex, 13px/16px padding). */
function CardHead({ className, ...props }: CardProps): ReactElement {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 border-b border-border px-4 py-[13px]',
        className,
      )}
      {...props}
    />
  );
}

/** `.card-eyebrow` — a tiny uppercase tracking label. */
function CardEyebrow({ className, ...props }: HTMLAttributes<HTMLParagraphElement>): ReactElement {
  return (
    <p
      className={cn(
        'text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-faint',
        className,
      )}
      {...props}
    />
  );
}

interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Heading level. Defaults to `h2` since a Card is a page-level section under
   *  the page's `h1`; override (e.g. `h3`) for nested cards to keep the outline. */
  as?: 'h2' | 'h3' | 'h4';
}

function CardTitle({ className, as: Tag = 'h2', ...props }: CardTitleProps): ReactElement {
  return (
    <Tag
      className={cn('text-[13.5px] font-bold tracking-[0.01em] text-text', className)}
      {...props}
    />
  );
}

function CardBody({ className, ...props }: CardProps): ReactElement {
  return <div className={cn('px-[18px] py-4', className)} {...props} />;
}

function CardFooter({ className, ...props }: CardProps): ReactElement {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-border px-4 py-3',
        className,
      )}
      {...props}
    />
  );
}

Card.Head = CardHead;
Card.Header = CardHead;
Card.Eyebrow = CardEyebrow;
Card.Title = CardTitle;
Card.Body = CardBody;
Card.Footer = CardFooter;
